import React, { createContext, useContext, useReducer, useEffect } from 'react';
import portalAuthService from '../services/portalAuthService';
import { clearPortalData, setSurface } from '../utils/portalStorage';
import { setLocaleFromUserLanguage } from '../i18n';

/**
 * Auth context for the CUSTOMER PORTAL. Mirrors the staff authContext but is a
 * completely separate provider tree — it holds the portal session only and
 * never touches the staff `auth_token`.
 */

const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_CUSTOMER: 'SET_CUSTOMER',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

const initialState = {
  isAuthenticated: false,
  customer: null,
  loading: true,
  error: null,
  // Whether `customer` reflects a LIVE /portal/me fetch (vs a possibly-stale
  // localStorage restore on boot). The onboarding router waits for this before
  // rendering a non-approved onboarding screen, so a hard-refresh doesn't flash
  // the wrong screen when server state has moved past what was stored.
  profileFresh: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload, error: null };
    case ACTIONS.LOGIN_SUCCESS:
      // `fresh` is true when the customer came from a live /me (fresh login),
      // false when restored from storage on boot.
      return { ...state, isAuthenticated: true, customer: action.payload.customer, profileFresh: !!action.payload.fresh, loading: false, error: null };
    case ACTIONS.LOGIN_FAILURE:
      return { ...state, isAuthenticated: false, customer: null, profileFresh: false, loading: false, error: action.payload.error };
    case ACTIONS.SET_CUSTOMER:
      // SET_CUSTOMER only fires from a live getProfile() — so the profile is fresh.
      return { ...state, customer: action.payload.customer, profileFresh: true };
    case ACTIONS.LOGOUT:
      return { ...state, isAuthenticated: false, customer: null, profileFresh: false, loading: false, error: null };
    case ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };
    default:
      return state;
  }
};

const PortalAuthContext = createContext(null);

export const PortalAuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Boot: restore a local portal session if the token is still valid.
  useEffect(() => {
    const status = portalAuthService.checkPortalAuth();
    if (status.isAuthenticated) {
      // Restored from storage — mark NOT fresh until /me confirms.
      dispatch({ type: ACTIONS.LOGIN_SUCCESS, payload: { customer: status.user, fresh: false } });
      // Refresh the profile in the background (also re-applies language).
      portalAuthService.getProfile().then((res) => {
        if (res.success) {
          dispatch({ type: ACTIONS.SET_CUSTOMER, payload: { customer: res.data } });
        } else if (res.status !== 401) {
          // /me failed for a non-auth reason (network error → no status, or 5xx).
          // Don't leave the shell stuck on the onboarding loader — fall back to
          // the stored state (marks profileFresh so a screen renders).
          dispatch({ type: ACTIONS.SET_CUSTOMER, payload: { customer: status.user } });
        }
        // 401 is handled by the api client (session-expired → login).
      });
    } else {
      clearPortalData();
      dispatch({ type: ACTIONS.LOGOUT });
    }
  }, []);

  // If any portal request 401s, the api client fires this — drop to login.
  useEffect(() => {
    const onExpired = () => dispatch({ type: ACTIONS.LOGOUT });
    window.addEventListener('portal-session-expired', onExpired);
    return () => window.removeEventListener('portal-session-expired', onExpired);
  }, []);

  // A 403 on a transactional endpoint means access changed server-side (e.g.
  // approval revoked). Re-fetch /me so the shell re-gates to the right screen.
  useEffect(() => {
    const onAccessChanged = () => {
      portalAuthService.getProfile().then((res) => {
        if (res.success) dispatch({ type: ACTIONS.SET_CUSTOMER, payload: { customer: res.data } });
      });
    };
    window.addEventListener('portal-access-changed', onAccessChanged);
    return () => window.removeEventListener('portal-access-changed', onAccessChanged);
  }, []);

  const login = async (identifier, password) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    const result = await portalAuthService.login(identifier, password);
    if (result.success) {
      const customer = result.data?.user || null;
      // Apply the customer's UI language if the profile carries one.
      if (customer?.language) {
        try { setLocaleFromUserLanguage(customer.language); } catch (e) { /* ignore */ }
      }
      // Fresh login fetched /me, so `customer` (when present) is fresh.
      dispatch({ type: ACTIONS.LOGIN_SUCCESS, payload: { customer, fresh: !!customer } });
      // If /me failed during login, the customer is null — retry once so the
      // shell (company name, etc.) fills in without a full reload.
      if (!customer) {
        portalAuthService.getProfile().then((res) => {
          if (res.success) {
            if (res.data?.language) {
              try { setLocaleFromUserLanguage(res.data.language); } catch (e) { /* ignore */ }
            }
            dispatch({ type: ACTIONS.SET_CUSTOMER, payload: { customer: res.data } });
          }
        });
      }
      return { success: true };
    }
    dispatch({ type: ACTIONS.LOGIN_FAILURE, payload: { error: result.error } });
    return result; // carries error/status/locked/rateLimited for the login screen
  };

  const logout = async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    await portalAuthService.logout();
    dispatch({ type: ACTIONS.LOGOUT });
    // Return the whole app to the staff surface (default) on logout.
    setSurface('staff');
    return { success: true };
  };

  const refreshCustomer = async () => {
    const res = await portalAuthService.getProfile();
    if (res.success) dispatch({ type: ACTIONS.SET_CUSTOMER, payload: { customer: res.data } });
    return res;
  };

  const clearError = () => dispatch({ type: ACTIONS.CLEAR_ERROR });

  const value = {
    ...state,
    isLoading: state.loading,
    login,
    logout,
    refreshCustomer,
    clearError,
  };

  return <PortalAuthContext.Provider value={value}>{children}</PortalAuthContext.Provider>;
};

export const usePortalAuth = () => {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error('usePortalAuth must be used within a PortalAuthProvider');
  return ctx;
};

export default PortalAuthContext;
